import { useCallback, useLayoutEffect, useMemo } from 'react';

import Breadcrumb, { BreadcrumbList } from 'components/shared/Breadcrumb/Breadcrumb';
import { useBreadcrumbDispatch } from 'components/shared/Breadcrumb/context/BreadcrumbContext';

// Stable empty steps prop: the shared BreadcrumbProvider resets its active index
// to the last step whenever the `steps` prop changes shape. Wizard consumers own
// the active/isFinished/hidden flags and mutate them mid-flow, so we keep the prop
// referentially stable (provider's reset never fires) and drive setSteps/setStep
// ourselves to stay fully controlled by the parent.
const STABLE_STEPS = [];

const ControlledBreadcrumbList = ({ breadcrumbs, setBreadCrumbs, enableNavigation }) => {
  const { setSteps, setStep } = useBreadcrumbDispatch();

  const visibleCrumbs = useMemo(() => breadcrumbs.filter((crumb) => !crumb?.hidden), [breadcrumbs]);

  const steps = useMemo(
    () =>
      visibleCrumbs.map((crumb) => ({
        name: String(crumb.view),
        title: crumb.title,
        view: crumb.view
      })),
    [visibleCrumbs]
  );

  const activeIndex = useMemo(() => {
    const index = visibleCrumbs.findIndex((crumb) => crumb.active);
    return index === -1 ? 0 : index;
  }, [visibleCrumbs]);

  useLayoutEffect(() => {
    setSteps(steps);
    setStep(activeIndex);
  }, [steps, activeIndex, setSteps, setStep]);

  const handleStepClick = useCallback(
    (_index, step) => {
      const originalIndex = breadcrumbs.findIndex((crumb) => crumb.view === step.view);
      if (originalIndex === -1) return;

      const previousIndex = originalIndex > 0 ? originalIndex - 1 : 0;
      const canNavigate = enableNavigation || breadcrumbs[previousIndex]?.isFinished;
      if (!canNavigate) return;

      setBreadCrumbs((prevBreadcrumbs) =>
        prevBreadcrumbs.map((crumb, idx) => ({
          ...crumb,
          active: idx === originalIndex
        }))
      );
    },
    [breadcrumbs, enableNavigation, setBreadCrumbs]
  );

  return <BreadcrumbList onStepClick={handleStepClick} />;
};

const WizardBreadcrumbs = (props) => (
  <Breadcrumb steps={STABLE_STEPS}>
    <ControlledBreadcrumbList {...props} />
  </Breadcrumb>
);

export default WizardBreadcrumbs;
